import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Avatar from '@mui/material/Avatar';
import MenuItem from '@mui/material/MenuItem';
import { Button, Link, TextField, Tooltip } from '@mui/material';
import { useRootStore } from '../hooks/useRootStore';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import ActiveIssue from './ActiveIssue';
import { IssuePriority } from '../data/IssuePriority';
import { deepOrange } from '@mui/material/colors';
import SearchIcon from '@mui/icons-material/Search';
import { UserType } from '../data/UserType';
import { CustomerEmployeeProfile } from '../data/UserProfile';
import './Header.css';
import useDeviceMedia from '../hooks/useDeviceMedia';

const Header = observer(() => {
    const { api, isLogged, userProfile } = useRootStore();
    const { isMobile } = useDeviceMedia();

    const settings = ['Мой профиль'];

    if (userProfile?.UserType == UserType.CustomerEmployee) {
        settings.push('Моя организация');
    }

    const [anchorNewIssue, setAnchorNewIssue] = useState<null | HTMLElement>(null);
    const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
    const [myName, setMyName] = useState<string | null>(null); 
    const [searchId, setSearchId] = useState<number | null>(null); 

    if (isLogged) {
        settings.push('Выйти');
    }

    const pages = [] as { link: string, caption: string, }[];

    if (isMobile) {
        pages.push({ link: 'dashboard', caption: "Дашборд" });
    }
    pages.push({ link: 'issues', caption: "Задачи" });

    if (userProfile?.UserType == UserType.Employee) {
        pages.push({ link: 'clients', caption: "Клиенты" });
    } else if (isMobile) {
        pages.push({ link: `clients/${(userProfile as CustomerEmployeeProfile).CustomerID}`, caption: "Моя организация" });
    }

    pages.push({ link: 'docs', caption: "Справка" });

    if (isMobile) {
        pages.push({ link: `employees/${userProfile!.ID}`, caption: "Мой профиль" });
    }

    const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorElNav(event.currentTarget);
    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorElUser(event.currentTarget);
    const handleCloseNavMenu = () => setAnchorElNav(null);

    const handleCloseUserMenu = (target: HTMLElement) => {
        if (target.textContent === 'Выйти') {
            api.signOut();
        }
        setAnchorElUser(null);
    };

    const newIssueMenuOpen = Boolean(anchorNewIssue);
    const newIssueMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorNewIssue(event.currentTarget);
        event.preventDefault();
        return false;
    };

    useEffect(() => {
        if (userProfile) {
            setMyName(userProfile.FirstName[0].toLowerCase());
        } else {
            setMyName(null);
        }
    }, [userProfile]);

    const createStubIssue = (authorId: number, priorityId: number, isError: boolean) => {
        api.createStubIssue(authorId, priorityId, isError).then(issueId => {
            setAnchorNewIssue(null);
            window.location.href = `issues/${issueId}`;
        });
    }

    return (
        <AppBar position="sticky" className="header">
            <ActiveIssue />
            <Toolbar className="header-main-area" sx={{ alignItems: 'center' }}>
                <Link href="/dashboard" title="Моя статистика" sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }}>
                    <img src='./logo1.svg' style={{ width: 135 }} />
                </Link>

                <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
                    <IconButton
                        size="large"
                        aria-label="account of current user"
                        aria-controls="menu-appbar"
                        aria-haspopup="true"
                        onClick={handleOpenNavMenu}
                        color="inherit"
                    >
                        <MenuIcon />
                    </IconButton>
                    <Menu
                        id="menu-appbar"
                        anchorEl={anchorElNav}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        keepMounted
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        open={Boolean(anchorElNav)}
                        onClose={handleCloseNavMenu}
                        sx={{ display: { xs: 'block', md: 'none' } }}
                    >
                        {pages.map((page) => (
                            <MenuItem key={page.link} onClick={handleCloseNavMenu}>
                                <Link href={page.link}>{page.caption}</Link>
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>
                <Box className="main-menu" sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                    {userProfile ?
                        <>
                            <Button
                                id="create-issue-btn"
                                key='create-new-issue'
                                aria-controls={newIssueMenuOpen ? 'new-issue--menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={newIssueMenuOpen ? 'true' : undefined}
                                onClick={newIssueMenuClick}
                                sx={{ my: 2, ml:3, color: 'white', display: 'block', textTransform:'initial' }}
                            >
                                Создать задачу
                            </Button>
                            <Menu
                                id="new-issue-menu"

                                anchorEl={anchorNewIssue}
                                onClose={() => setAnchorNewIssue(null)}
                                open={newIssueMenuOpen}
                                MenuListProps={{
                                    'aria-labelledby': 'basic-button',
                                }}
                            >
                                <MenuItem key='create-new-issue-standard' onClick={() => createStubIssue(userProfile.ID, IssuePriority.standard, false)}>Обычную</MenuItem>
                                <MenuItem key='create-new-issue-priority' onClick={() => createStubIssue(userProfile.ID, IssuePriority.priority, false)}>Приоритетную</MenuItem>
                                <MenuItem key='create-new-issue-critical' onClick={() => createStubIssue(userProfile.ID, IssuePriority.critical, false)}>Критическую</MenuItem>
                                <MenuItem key='create-new-issue-error' onClick={() => createStubIssue(userProfile.ID, IssuePriority.standard, true)}>Ошибку</MenuItem>
                            </Menu>
                        </>
                        : ''}
                    {pages.map((page) => (
                        <Link
                            href={"./" + page.link}
                            key={page.link}
                            onClick={handleCloseNavMenu}
                            sx={{ my: 2, color: 'white', display: 'block' }}
                        >
                            {page.caption}
                        </Link>
                    ))}
                </Box>
                <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', alignContent: 'center' }}>
                    <Box className="search-issue" sx={{ position: 'relative'}}>
                        <TextField
                            className="text"
                            onBeforeInput={(e: React.CompositionEvent<HTMLInputElement>) => {
                                if (isNaN(parseInt(e.data))) {
                                    e.preventDefault();
                                }
                            }}
                            onChange={(e) => {
                                const id = parseFloat(e.target.value);
                                setSearchId(id as number);
                            }}
                            onKeyDown={(e) => {
                                if (e.key == 'Enter' && searchId) {
                                    window.location.href = `issues/${searchId}`;
                                }
                            }}
                            variant='outlined'
                            sx={{ width: 170 }}
                            placeholder="к задаче..."
                            size="small"
                        />
                        <IconButton disabled={!searchId} aria-label="search" sx={{ position: 'absolute', right: 0, cursor: 'pointer' }}>
                            <Tooltip title="Перейти к заявке">
                                <SearchIcon onClick={() => { if (searchId) { window.location.href = `issues/${searchId}` } }} />
                            </Tooltip>
                        </IconButton>
                    </Box>
                    {isMobile ? '' :
                        <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                            {myName
                                ? <Avatar sx={{ bgcolor: deepOrange[500] }} src={userProfile?.Avatar}>{myName}</Avatar>
                                : <Avatar alt="Профиль пользователя"></Avatar>
                            }
                        </IconButton>
                    }
                    <Menu
                        sx={{ mt: '45px' }}
                        id="menu-appbar"
                        anchorEl={anchorElUser}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        keepMounted
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        open={Boolean(anchorElUser)}
                        onClose={handleCloseUserMenu}
                    >
                        {settings.map((setting) => (
                            <MenuItem key={setting} onClick={(e) => handleCloseUserMenu(e.target as HTMLElement)}>
                                {setting == 'Мой профиль'
                                    ? <Link sx={{ background: 'none' }} href={`employees/${userProfile?.ID}`}>Мой профиль</Link>
                                    : setting == 'Моя организация'
                                        ? <Link sx={{ background: 'none' }} href={`clients/${(userProfile as CustomerEmployeeProfile).CustomerID}`}>Моя организация</Link>
                                    : setting}
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    );
})

export default Header;